import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  flattenTrainingTags,
  trainingApi,
  trainingBlockApi,
  trainingProgramActivationApi,
  trainingProgramApi,
  userApi,
} from '../../api'
import { subscriptionApi } from '../../api/subscriptionApi'
import type { TrainingTagDto } from '../../api/trainingBlockApi'
import { workoutApi } from '../../api/workoutApi'
import type {
  CoachTodayLiveTrainee,
  WorkoutExerciseHistoryEntry,
  WorkoutSession,
} from '../../api/workoutApi'
import type { ApiError } from '../../api/client'
import type { ThisWeekTraining, TrainingProgram, TrainingProgramActivation, UserRelation } from '../../types/api'
import { captureEvent } from '../../posthog'
import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../i18n'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf } from '../../utils/entitlements'
import { formatLocalDateTimeForAPI, formatTraineeDisplayName } from '../../utils/trainingUtils'
import { Avatar, Button, ErrorMessage } from '../ui'
import { WorkoutSessionActiveView } from './WorkoutSessionActiveView'
import {
  logActualFromExercise,
  type LogActuals,
} from './workoutLogActuals'
import { countExercisesInItems, sortExercisesInItem } from './blockItemHelpers'
import { BlockIndexLabel } from './BlockIndexLabel'
import {
  groupIndexForLogId,
  groupWorkoutExercises,
} from './workoutGroupHelpers'
import {
  coachWorkoutMutations,
  dirtyLogIdsFromActuals,
  mergeActualsPreservingDirty,
  mergeServerSession,
  refreshAndSetCurrent,
  saveDirtyLogs,
} from './workoutSessionSync'

interface OverlapGroup {
  sessions: ThisWeekTraining[]
  timeLabel: string
}

const pad = (n: number) => String(n).padStart(2, '0')
const SAVE_DEBOUNCE_MS = 400
const VISIBLE_BLOCKS = 3
const UNPLANNED_CTA_KEY = 'unplanned-cta'

function makeSyntheticUnplannedSession(opts: {
  traineeId: number
  firstName?: string | null
  lastName?: string | null
  username?: string | null
  photoUrl?: string | null
}): ThisWeekTraining {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0, 0, 0)
  const end = new Date(start)
  end.setHours(end.getHours() + 1)
  return {
    projectionId: -opts.traineeId,
    setId: 0,
    start: start.toISOString(),
    end: end.toISOString(),
    status: 'UNPLANNED',
    otherUserId: opts.traineeId,
    otherUserFirstName: opts.firstName ?? null,
    otherUserLastName: opts.lastName ?? null,
    otherUserUsername: opts.username ?? null,
    otherUserPhotoUrl: opts.photoUrl ?? null,
    isCoach: true,
  }
}

function buildHourlyGroups(sessions: ThisWeekTraining[]): OverlapGroup[] {
  if (sessions.length === 0) return []
  const minHour = Math.min(...sessions.map(s => new Date(s.start).getHours()))
  const maxHour = Math.max(...sessions.map(s => new Date(s.end).getHours()))
  const groups: OverlapGroup[] = []
  for (let h = minHour; h <= maxHour; h++) {
    const base = new Date()
    const windowStart = new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, 0, 0, 0)
    const windowEnd = new Date(base.getFullYear(), base.getMonth(), base.getDate(), h + 1, 0, 0, 0)
    const inWindow = sessions.filter(s =>
      new Date(s.start) < windowEnd && new Date(s.end) > windowStart,
    )
    if (inWindow.length > 0) {
      groups.push({ sessions: inWindow, timeLabel: `${pad(h)}:00 – ${pad(h + 1)}:00` })
    }
  }
  return groups
}

function findDefaultGroupIndex(groups: OverlapGroup[]): number {
  const now = Date.now()
  for (let i = 0; i < groups.length; i++) {
    const minStart = Math.min(...groups[i].sessions.map(s => new Date(s.start).getTime()))
    const maxEnd = Math.max(...groups[i].sessions.map(s => new Date(s.end).getTime()))
    if (now >= minStart && now <= maxEnd) return i
  }
  for (let i = 0; i < groups.length; i++) {
    const minStart = Math.min(...groups[i].sessions.map(s => new Date(s.start).getTime()))
    if (minStart > now) return i
  }
  return 0
}

function isLiveActive(row: CoachTodayLiveTrainee | undefined): boolean {
  return Boolean(row?.activeSession && !row.activeSession.finishedAt)
}

function findSessionAcrossGroups(groups: OverlapGroup[], traineeId: number): ThisWeekTraining | undefined {
  for (const group of groups) {
    const hit = group.sessions.find(s => s.otherUserId === traineeId)
    if (hit) return hit
  }
  return undefined
}

function sessionFromLiveTrainee(row: CoachTodayLiveTrainee): ThisWeekTraining {
  return makeSyntheticUnplannedSession({
    traineeId: row.traineeId,
    firstName: row.firstName,
    lastName: row.lastName,
    username: row.username,
    photoUrl: row.photoUrl,
  })
}

/**
 * Build carousel columns for the selected timeslot.
 * Trainees with an unfinished live workout are always pinned first so the coach
 * cannot switch slots and forget to finish them.
 */
function buildCarouselSessions(opts: {
  slotSessions: ThisWeekTraining[]
  allGroups: OverlapGroup[]
  unplannedSessions: ThisWeekTraining[]
  liveByTrainee: Map<number, CoachTodayLiveTrainee>
}): ThisWeekTraining[] {
  const { slotSessions, allGroups, unplannedSessions, liveByTrainee } = opts
  const seen = new Set<number>()
  const ordered: ThisWeekTraining[] = []

  function push(session: ThisWeekTraining) {
    if (seen.has(session.otherUserId)) return
    seen.add(session.otherUserId)
    ordered.push(session)
  }

  for (const [traineeId, row] of liveByTrainee) {
    if (!isLiveActive(row)) continue
    const session =
      findSessionAcrossGroups(allGroups, traineeId)
      ?? unplannedSessions.find(s => s.otherUserId === traineeId)
      ?? sessionFromLiveTrainee(row)
    push(session)
  }

  for (const session of slotSessions) {
    push(session)
  }

  for (const session of unplannedSessions) {
    push(session)
  }

  return ordered
}

function fmtTime(iso: string) {
  const d = new Date(iso)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function indexFromSession(data: WorkoutSession): number {
  const groups = groupWorkoutExercises(data.exercises)
  if (!groups.length) return 0
  return groupIndexForLogId(groups, data.currentLogId)
}

function columnStridePx() {
  return window.innerWidth * 0.75 + 12
}

function clampOffset(value: number, count: number) {
  const max = Math.max(0, (count - 1) * columnStridePx())
  return Math.min(max, Math.max(0, value))
}

export default function ProViewLive() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user, refreshUser } = useAuth()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [overlapGroups, setOverlapGroups] = useState<OverlapGroup[]>([])
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [liveByTrainee, setLiveByTrainee] = useState<Map<number, CoachTodayLiveTrainee>>(new Map())
  const [programsByTrainee, setProgramsByTrainee] = useState<Map<number, {
    activation: TrainingProgramActivation
    program: TrainingProgram
  }>>(new Map())
  const [availableTags, setAvailableTags] = useState<TrainingTagDto[]>([])
  const [unplannedSessions, setUnplannedSessions] = useState<ThisWeekTraining[]>([])
  const [showUnplannedPicker, setShowUnplannedPicker] = useState(false)
  const [trialBusy, setTrialBusy] = useState(false)
  const canProView = entitlementsOf(user).proView
  const trialAvailable = Boolean(user?.trialAvailable)
  const unplannedSessionsRef = useRef(unplannedSessions)
  unplannedSessionsRef.current = unplannedSessions

  const loadAll = useCallback(async (opts?: { quiet?: boolean }) => {
    if (!entitlementsOf(user).proView) {
      setLoading(false)
      return
    }
    if (!opts?.quiet) setLoading(true)
    setError(null)
    try {
      const now = new Date()
      const todayStart = new Date(now)
      todayStart.setHours(0, 0, 0, 0)
      const todayEnd = new Date(now)
      todayEnd.setHours(23, 59, 59, 999)

      const [sessions, live] = await Promise.all([
        trainingApi.getTrainingsInRange(
          formatLocalDateTimeForAPI(todayStart),
          formatLocalDateTimeForAPI(todayEnd),
        ),
        workoutApi.getCoachTodayLive(),
      ])

      const coachSessions = sessions.filter(s => s.isCoach)
      const groups = buildHourlyGroups(coachSessions)
      setOverlapGroups(groups)
      setSelectedIdx(prev => {
        if (groups.length === 0) return 0
        if (prev >= 0 && prev < groups.length) return prev
        return findDefaultGroupIndex(groups)
      })

      const liveMap = new Map<number, CoachTodayLiveTrainee>()
      for (const row of live.trainees) {
        liveMap.set(row.traineeId, row)
      }
      setLiveByTrainee(liveMap)

      const scheduledIds = new Set(coachSessions.map(s => s.otherUserId))
      const walkInFromLive = live.trainees
        .filter(row =>
          !scheduledIds.has(row.traineeId)
          && row.activeSession
          && !row.activeSession.finishedAt,
        )
        .map(row => makeSyntheticUnplannedSession({
          traineeId: row.traineeId,
          firstName: row.firstName,
          lastName: row.lastName,
          username: row.username,
          photoUrl: row.photoUrl,
        }))

      const nextUnplannedMap = new Map<number, ThisWeekTraining>()
      for (const s of unplannedSessionsRef.current) {
        if (!scheduledIds.has(s.otherUserId)) nextUnplannedMap.set(s.otherUserId, s)
      }
      for (const s of walkInFromLive) {
        if (!nextUnplannedMap.has(s.otherUserId)) nextUnplannedMap.set(s.otherUserId, s)
      }
      const nextUnplanned = [...nextUnplannedMap.values()]
      setUnplannedSessions(nextUnplanned)

      const traineeIds = [...new Set([
        ...coachSessions.map(s => s.otherUserId),
        ...nextUnplanned.map(s => s.otherUserId),
      ])]
      const programEntries = await Promise.all(traineeIds.map(async (traineeId) => {
        try {
          const activation = await trainingProgramActivationApi.getTraineeActiveAssignment(traineeId)
          if (!activation) return null
          const program = await trainingProgramApi.getProgram(activation.programId)
          return [traineeId, { activation, program }] as const
        } catch {
          return null
        }
      }))
      const programMap = new Map<number, { activation: TrainingProgramActivation; program: TrainingProgram }>()
      for (const entry of programEntries) {
        if (entry) programMap.set(entry[0], entry[1])
      }
      setProgramsByTrainee(programMap)
    } catch (err) {
      console.error('Error loading pro view live:', err)
      setError(err instanceof Error ? err.message : t.proViewLive.error.message)
    } finally {
      setLoading(false)
    }
  }, [t.proViewLive.error.message, user])

  useEffect(() => {
    trainingBlockApi.getTags().then(g => setAvailableTags(flattenTrainingTags(g))).catch(() => { })
  }, [])

  useEffect(() => {
    if (!user?.hasTrainees) {
      navigate('/more', { replace: true })
      return
    }
    void loadAll()
  }, [user?.hasTrainees, navigate, loadAll])

  const selectedGroup = overlapGroups[selectedIdx]
  const hasSchedule = overlapGroups.length > 0
  const effectiveGroup: OverlapGroup = hasSchedule && selectedGroup
    ? selectedGroup
    : { sessions: [], timeLabel: t.proViewLive.unplanned.timeLabel }
  const carouselSessions = buildCarouselSessions({
    slotSessions: effectiveGroup.sessions,
    allGroups: overlapGroups,
    unplannedSessions,
    liveByTrainee,
  })
  const unplannedIdSet = new Set(unplannedSessions.map(s => s.otherUserId))
  const todayScheduledIds = new Set(overlapGroups.flatMap(g => g.sessions.map(s => s.otherUserId)))
  const boardIds = new Set([
    ...todayScheduledIds,
    ...unplannedSessions.map(s => s.otherUserId),
    ...[...liveByTrainee.values()].filter(isLiveActive).map(r => r.traineeId),
  ])
  const tagEmojiMap = Object.fromEntries(availableTags.map(tag => [tag.key, tag.emoji]))
  const sessionCount = carouselSessions.length + 1
  const [offsetX, setOffsetX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const offsetRef = useRef(0)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    originOffset: number
    axis: 'x' | 'y' | null
  } | null>(null)

  function setOffset(value: number, count = sessionCount) {
    const next = clampOffset(value, count)
    offsetRef.current = next
    setOffsetX(next)
  }

  useEffect(() => {
    setIsDragging(false)
    setOffset(0, sessionCount)
    // Reset only when switching time groups, not when walk-ins are added
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdx])

  useEffect(() => {
    const next = clampOffset(offsetRef.current, sessionCount)
    offsetRef.current = next
    setOffsetX(next)
  }, [sessionCount])

  async function handlePickUnplannedTrainee(trainee: UserRelation) {
    if (boardIds.has(trainee.id)) {
      setShowUnplannedPicker(false)
      return
    }
    try {
      const activation = await trainingProgramActivationApi.getTraineeActiveAssignment(trainee.id)
      if (!activation) {
        setError(t.proViewLive.unplanned.addFailed)
        return
      }
      const program = await trainingProgramApi.getProgram(activation.programId)
      const synthetic = makeSyntheticUnplannedSession({
        traineeId: trainee.id,
        firstName: trainee.firstName,
        lastName: trainee.lastName,
        username: trainee.username,
        photoUrl: trainee.photoUrl,
      })
      setUnplannedSessions(prev => [...prev, synthetic])
      setProgramsByTrainee(prev => {
        const map = new Map(prev)
        map.set(trainee.id, { activation, program })
        return map
      })
      setShowUnplannedPicker(false)
      // Scroll toward the new column (before CTA)
      requestAnimationFrame(() => {
        const count = carouselSessions.length + 2
        setOffset((count - 2) * columnStridePx(), count)
      })
    } catch (err) {
      console.error('Error adding unplanned trainee:', err)
      setError(err instanceof Error ? err.message : t.proViewLive.unplanned.addFailed)
    }
  }

  function onCarouselPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (sessionCount <= 1) return
    const target = e.target as HTMLElement
    if (target.closest('button, input, textarea, a, [role="button"]')) return

    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originOffset: offsetRef.current,
      axis: null,
    }
  }

  function onCarouselPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return

    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY

    if (drag.axis == null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      if (drag.axis === 'x') {
        setIsDragging(true)
        e.currentTarget.setPointerCapture(e.pointerId)
      } else {
        dragRef.current = null
        return
      }
    }

    if (drag.axis === 'x') {
      e.preventDefault()
      setOffset(drag.originOffset - dx)
    }
  }

  function onCarouselPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    dragRef.current = null
    if (drag.axis !== 'x') return

    const stride = columnStridePx()
    const snapped = Math.round(offsetRef.current / stride) * stride
    setIsDragging(false)
    setOffset(snapped)
  }

  const trackStyle: React.CSSProperties | undefined = sessionCount > 0
    ? {
      display: 'flex',
      gap: 12,
      width: `calc(${sessionCount} * 75vw + ${(sessionCount - 1) * 12}px)`,
      transform: `translate3d(-${offsetX}px, 0, 0)`,
      transition: isDragging ? 'none' : 'transform 0.22s ease-out',
      willChange: 'transform',
    }
    : undefined

  const columnStyle: React.CSSProperties = {
    flex: '0 0 75vw',
    width: '75vw',
    maxWidth: '75vw',
  }

  if (!canProView) {
    const startTrial = async () => {
      try {
        setTrialBusy(true)
        await subscriptionApi.activateTrial()
        await refreshUser()
      } catch (err) {
        console.error('Failed to activate trial:', err)
      } finally {
        setTrialBusy(false)
      }
    }

    return (
      <div className="p-5">
        <header className="mb-5">
          <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
            {t.proViewLive.title}
          </h1>
          <p className="text-sm" style={{ color: theme.text.muted }}>
            {t.subscription.limitReached.proView}
          </p>
        </header>
        {trialAvailable && (
          <Button fullWidth variant="brand" className="mb-3" onClick={() => void startTrial()} disabled={trialBusy}>
            {trialBusy ? t.common.loading : t.subscription.trial.activate}
          </Button>
        )}
        <Button fullWidth onClick={() => navigate('/subscription')}>
          {t.subscription.unlockCta}
        </Button>
      </div>
    )
  }

  return (
    <div className="p-5">
      <header className="mb-5">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.proViewLive.title}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.proViewLive.subtitle}
        </p>
      </header>

      {loading ? (
        <div className="flex justify-center py-10">
          <span style={{ color: theme.text.muted }}>{t.common.loading}</span>
        </div>
      ) : error && overlapGroups.length === 0 && unplannedSessions.length === 0 && !showUnplannedPicker ? (
        <ErrorMessage title={t.proViewLive.error.title} message={error} icon="⚠️" onRetry={() => void loadAll()} />
      ) : (
        <>
          {error && (
            <p className="text-xs mb-3 rounded-xl px-3 py-2" style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: theme.text.primary }}>
              {error}
            </p>
          )}

          {hasSchedule ? (
            <div
              className="flex items-center justify-between mb-4 px-4 py-3 rounded-xl"
              style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold" style={{ color: theme.text.primary }}>
                  {effectiveGroup.timeLabel}
                </p>
                <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                  {selectedIdx + 1} / {overlapGroups.length}
                </p>
              </div>
              <div className="flex gap-2 ml-3 flex-shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={selectedIdx === 0}
                  onClick={() => setSelectedIdx(i => i - 1)}
                >
                  ↑
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={selectedIdx === overlapGroups.length - 1}
                  onClick={() => setSelectedIdx(i => i + 1)}
                >
                  ↓
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="mb-4 px-4 py-3 rounded-xl"
              style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
            >
              <p className="text-sm font-semibold" style={{ color: theme.text.primary }}>
                {effectiveGroup.timeLabel}
              </p>
              <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                {t.proView.emptyMessage}
              </p>
            </div>
          )}

          <div
            style={{ touchAction: 'pan-y' }}
            onPointerDown={onCarouselPointerDown}
            onPointerMove={onCarouselPointerMove}
            onPointerUp={onCarouselPointerUp}
            onPointerCancel={onCarouselPointerUp}
          >
            <div
              className="sticky z-20 -mx-5 overflow-hidden"
              style={{
                top: 0,
                paddingTop: 'env(safe-area-inset-top, 0px)',
                backgroundColor: theme.bgColor,
              }}
            >
              <div className="px-5 pb-3">
                <div style={trackStyle}>
                  {carouselSessions.map((session) => {
                    const live = liveByTrainee.get(session.otherUserId)
                    const active = live?.activeSession && !live.activeSession.finishedAt
                    return (
                      <div key={session.otherUserId} style={columnStyle}>
                        <TraineeHeaderCard
                          session={session}
                          isLive={Boolean(active)}
                        />
                      </div>
                    )
                  })}
                  <div key={UNPLANNED_CTA_KEY} style={columnStyle}>
                    <UnplannedHeaderCard
                      onOpen={() => {
                        setError(null)
                        setShowUnplannedPicker(true)
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden -mx-1 px-1 pb-4">
              <div style={trackStyle}>
                {carouselSessions.map((session) => {
                  const live = liveByTrainee.get(session.otherUserId)
                  const programInfo = programsByTrainee.get(session.otherUserId)
                  const isUnplanned = unplannedIdSet.has(session.otherUserId)
                  return (
                    <div key={session.otherUserId} style={columnStyle}>
                      <TraineeLiveCard
                        session={session}
                        live={live}
                        programInfo={programInfo}
                        tagEmojiMap={tagEmojiMap}
                        unplanned={isUnplanned}
                        onSessionChange={(traineeId, next) => {
                          setLiveByTrainee(prev => {
                            const map = new Map(prev)
                            const existing = map.get(traineeId)
                            map.set(traineeId, {
                              traineeId,
                              firstName: existing?.firstName ?? session.otherUserFirstName ?? null,
                              lastName: existing?.lastName ?? session.otherUserLastName ?? null,
                              username: existing?.username ?? session.otherUserUsername ?? null,
                              photoUrl: existing?.photoUrl ?? session.otherUserPhotoUrl ?? null,
                              activeSession: next,
                            })
                            return map
                          })
                        }}
                        onRefresh={() => void loadAll({ quiet: true })}
                      />
                    </div>
                  )
                })}
                {/* Spacer keeps carousel track width aligned with sticky header CTA column */}
                <div key={UNPLANNED_CTA_KEY} style={columnStyle} aria-hidden />
              </div>
            </div>

            {sessionCount > 1 && (
              <div className="flex items-center justify-center gap-3 pb-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offsetX <= 0}
                  onClick={() => setOffset(offsetRef.current - columnStridePx())}
                >
                  ←
                </Button>
                <span className="text-xs tabular-nums" style={{ color: theme.text.muted }}>
                  {Math.round(offsetX / columnStridePx()) + 1} / {sessionCount}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offsetX >= (sessionCount - 1) * columnStridePx() - 1}
                  onClick={() => setOffset(offsetRef.current + columnStridePx())}
                >
                  →
                </Button>
              </div>
            )}
          </div>

          {showUnplannedPicker && (
            <UnplannedTraineePicker
              excludedIds={boardIds}
              onSelect={(trainee) => void handlePickUnplannedTrainee(trainee)}
              onClose={() => setShowUnplannedPicker(false)}
            />
          )}
        </>
      )}
    </div>
  )
}

function UnplannedHeaderCard({ onOpen }: { onOpen: () => void }) {
  const theme = useTheme()
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left active:opacity-80 transition-opacity"
      style={{ backgroundColor: theme.cardBgLight, border: `1px dashed ${theme.cardBorder}` }}
    >
      {/* Match TraineeHeaderCard Avatar size="md" (w-14 h-14) */}
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0"
        style={{ backgroundColor: `${theme.linkColor}18`, color: theme.linkColor }}
      >
        +
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate" style={{ color: theme.text.primary }}>
          {t.proViewLive.unplanned.columnTitle}
        </p>
        <p className="text-xs" style={{ color: theme.text.muted }}>
          {t.proViewLive.unplanned.columnHint}
        </p>
      </div>
    </button>
  )
}

function UnplannedTraineePicker({
  excludedIds,
  onSelect,
  onClose,
}: {
  excludedIds: Set<number>
  onSelect: (trainee: UserRelation) => void
  onClose: () => void
}) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [options, setOptions] = useState<UserRelation[]>([])
  const [error, setError] = useState<string | null>(null)

  const excludedKey = [...excludedIds].sort((a, b) => a - b).join(',')
  useEffect(() => {
    let cancelled = false
    const excluded = new Set(
      excludedKey.length === 0 ? [] : excludedKey.split(',').map(Number),
    )
      ; (async () => {
        setLoading(true)
        setError(null)
        try {
          const trainees = await userApi.getTrainees()
          const eligible = await Promise.all(trainees.map(async (tr) => {
            if (excluded.has(tr.id)) return null
            try {
              const activation = await trainingProgramActivationApi.getTraineeActiveAssignment(tr.id)
              return activation ? tr : null
            } catch {
              return null
            }
          }))
          if (!cancelled) setOptions(eligible.filter((tr): tr is UserRelation => tr != null))
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : t.proViewLive.unplanned.addFailed)
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      })()
    return () => { cancelled = true }
  }, [excludedKey, t.proViewLive.unplanned.addFailed])

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[70vh] overflow-auto rounded-t-2xl sm:rounded-2xl p-4"
        style={{ backgroundColor: theme.bgColor }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold" style={{ color: theme.text.primary }}>
            {t.proViewLive.unplanned.pickerTitle}
          </h2>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t.proViewLive.unplanned.cancel}
          </Button>
        </div>

        {loading ? (
          <p className="text-sm py-6 text-center" style={{ color: theme.text.muted }}>
            {t.proViewLive.unplanned.pickerLoading}
          </p>
        ) : error ? (
          <p className="text-sm py-4" style={{ color: theme.text.primary }}>{error}</p>
        ) : options.length === 0 ? (
          <p className="text-sm py-6 text-center" style={{ color: theme.text.muted }}>
            {t.proViewLive.unplanned.pickerEmpty}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {options.map((tr) => {
              const name = [tr.firstName, tr.lastName].filter(Boolean).join(' ').trim()
                || tr.username
                || `#${tr.id}`
              return (
                <li key={tr.id}>
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left"
                    style={{ backgroundColor: theme.cardBgLight, border: `1px solid ${theme.cardBorder}` }}
                    onClick={() => onSelect(tr)}
                  >
                    <Avatar
                      photoUrl={tr.photoUrl}
                      name={name}
                      size="md"
                      fallbackBg={theme.brand.medium}
                    />
                    <span className="text-sm font-semibold truncate" style={{ color: theme.text.primary }}>
                      {name}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

function TraineeHeaderCard({
  session,
  isLive,
}: {
  session: ThisWeekTraining
  isLive: boolean
}) {
  const theme = useTheme()
  const { t } = useTranslation()
  const name = formatTraineeDisplayName(session)
  const timeRange = `${fmtTime(session.start)} – ${fmtTime(session.end)}`

  return (
    <div
      className="flex items-center gap-3 rounded-2xl px-4 py-3"
      style={{ backgroundColor: theme.cardBgLight, border: `1px solid ${theme.cardBorder}` }}
    >
      <Avatar
        photoUrl={session.otherUserPhotoUrl ?? undefined}
        name={name}
        size="md"
        fallbackBg={theme.brand.medium}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate" style={{ color: theme.text.primary }}>{name}</p>
        <p className="text-xs" style={{ color: theme.text.muted }}>{timeRange}</p>
      </div>
      {isLive && (
        <span
          className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full"
          style={{ backgroundColor: `${theme.linkColor}18`, color: theme.linkColor }}
        >
          {t.proViewLive.liveBadge}
        </span>
      )}
    </div>
  )
}

function TraineeLiveCard({
  session,
  live,
  programInfo,
  tagEmojiMap,
  unplanned = false,
  onSessionChange,
  onRefresh,
}: {
  session: ThisWeekTraining
  live?: CoachTodayLiveTrainee
  programInfo?: { activation: TrainingProgramActivation; program: TrainingProgram }
  tagEmojiMap: Record<string, string>
  unplanned?: boolean
  onSessionChange: (traineeId: number, session: WorkoutSession | null) => void
  onRefresh: () => void
}) {
  const theme = useTheme()
  const { t } = useTranslation()
  const active = live?.activeSession && !live.activeSession.finishedAt ? live.activeSession : null

  const [startingBlockId, setStartingBlockId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [showAllBlocks, setShowAllBlocks] = useState(false)

  const days = programInfo?.program.days ?? []

  useEffect(() => {
    setShowAllBlocks(false)
    setExpandedIds(new Set())
  }, [programInfo?.program.id])

  function toggleExpand(blockId: number) {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(blockId)) next.delete(blockId)
      else next.add(blockId)
      return next
    })
  }
  async function startBlock(blockId: number) {
    setStartingBlockId(blockId)
    setError(null)
    try {
      const created = await workoutApi.coachStartSession(
        session.otherUserId,
        blockId,
        programInfo?.activation.id ?? null,
        { unplanned },
      )
      captureEvent('workout_started', {
        session_id: created.id,
        block_id: blockId,
        trainee_id: session.otherUserId,
        source: 'pro_view',
        unplanned: Boolean(unplanned),
      })
      onSessionChange(session.otherUserId, created)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.proViewLive.error.startFailed)
    } finally {
      setStartingBlockId(null)
    }
  }

  const visibleDays = showAllBlocks ? days : days.slice(0, VISIBLE_BLOCKS)
  const hiddenCount = days.length - VISIBLE_BLOCKS

  return (
    <div>
      {error && (
        <p className="text-xs mb-3 rounded-xl px-3 py-2" style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: theme.text.primary }}>
          {error}
        </p>
      )}

      {active ? (
        <CoachLiveSessionPanel
          traineeId={session.otherUserId}
          session={active}
          onSessionChange={(next) => onSessionChange(session.otherUserId, next)}
          onFinished={onRefresh}
        />
      ) : !programInfo ? (
        <div
          className="rounded-2xl px-4 py-4"
          style={{ backgroundColor: theme.cardBgLight, border: `1px solid ${theme.cardBorder}` }}
        >
          <p className="text-sm font-semibold mb-0.5" style={{ color: theme.text.primary }}>{t.proView.noProgram}</p>
          <p className="text-xs" style={{ color: theme.text.muted }}>{t.proView.noProgramMessage}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {programInfo.program.name && (
            <p className="text-xs font-semibold px-1" style={{ color: theme.text.muted }}>
              {programInfo.program.name}
            </p>
          )}
          {visibleDays.map((day) => {
            const dayItems = day.items ?? []
            const exerciseCount = countExercisesInItems(dayItems)
            const tags = day.blockTags ?? []
            const isStarting = startingBlockId === day.blockId
            const expanded = expandedIds.has(day.blockId)
            return (
              <div
                key={day.blockId}
                className="rounded-2xl overflow-hidden"
                style={{ backgroundColor: theme.cardBgLight, border: `1px solid ${theme.cardBorder}` }}
              >
                <button
                  type="button"
                  aria-expanded={expanded}
                  className="w-full flex items-start gap-3 px-4 py-3.5 text-left active:opacity-80 transition-opacity"
                  onClick={() => toggleExpand(day.blockId)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="min-w-0">
                      <BlockIndexLabel index={day.sortOrder + 1} className="block mb-0.5" />
                      <p className="font-semibold truncate" style={{ color: theme.text.primary }}>
                        {day.blockName ?? `Block #${day.blockId}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums"
                        style={{
                          backgroundColor: theme.inputBgSubtle,
                          color: theme.text.muted,
                        }}
                      >
                        {t.trainingBlocks.exerciseCount_other.replace('{{count}}', String(exerciseCount))}
                      </span>
                      {tags.length > 0 && (
                        <span className="text-sm leading-none tracking-wide">
                          {tags.map(tag => tagEmojiMap[tag] ?? '').filter(Boolean).join(' ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0 mt-1 transition-transform duration-200"
                    style={{
                      color: theme.text.muted,
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                    aria-hidden
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {expanded && (
                  <>
                    {exerciseCount > 0 ? (
                      <div style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
                        {[...dayItems]
                          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                          .map((item, idx, arr) => {
                            const names = sortExercisesInItem(item.exercises)
                              .map(ex => ex.name?.trim())
                              .filter(Boolean) as string[]
                            if (names.length === 0) return null
                            const isLast = idx === arr.length - 1
                            const displayIndex = item.sortOrder != null ? item.sortOrder + 1 : idx + 1
                            const note = item.note?.trim()
                            return (
                              <div
                                key={item.id ?? `item-${idx}`}
                                className="flex items-start gap-3 px-4 py-2.5"
                                style={{ borderBottom: isLast ? undefined : `1px solid ${theme.cardBorder}` }}
                              >
                                <span
                                  className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
                                  style={{ backgroundColor: `${theme.buttonColor}18`, color: theme.accent.text }}
                                >
                                  {displayIndex}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                                    {names.join(' · ')}
                                  </p>
                                  {note && (
                                    <p className="text-xs mt-1 leading-relaxed" style={{ color: theme.text.muted }}>
                                      {note}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    ) : (
                      <div
                        className="px-4 py-3"
                        style={{ borderTop: `1px solid ${theme.cardBorder}` }}
                      >
                        <p className="text-sm text-center" style={{ color: theme.text.muted }}>
                          {t.trainingBlocks.noExercises}
                        </p>
                      </div>
                    )}

                    {tags.length > 0 && (
                      <div
                        className="flex flex-wrap gap-1.5 px-4 py-2.5"
                        style={{ borderTop: `1px solid ${theme.cardBorder}` }}
                      >
                        {tags.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: `${theme.buttonColor}12`,
                              border: `1px solid ${theme.buttonColor}25`,
                              color: theme.accent.text,
                            }}
                          >
                            <span className="leading-none">{tagEmojiMap[tag] ?? ''}</span>
                            <span>{t.trainingTags[tag as keyof typeof t.trainingTags] ?? tag}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}

                <div
                  className="px-3 py-2"
                  style={{ borderTop: `1px solid ${theme.cardBorder}` }}
                >
                  <Button
                    fullWidth
                    size="md"
                    disabled={startingBlockId != null}
                    onClick={() => void startBlock(day.blockId)}
                  >
                    {isStarting ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <span
                          className="inline-block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
                          aria-hidden
                        />
                        {t.proViewLive.starting}
                      </span>
                    ) : (
                      t.proViewLive.startWorkout
                    )}
                  </Button>
                </div>
              </div>
            )
          })}
          {!showAllBlocks && hiddenCount > 0 && (
            <button
              type="button"
              className="text-sm font-semibold py-2 active:opacity-70"
              style={{ color: theme.linkColor }}
              onClick={() => setShowAllBlocks(true)}
            >
              {t.proViewLive.moreBlocks.replace('{{count}}', String(hiddenCount))}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function CoachLiveSessionPanel({
  traineeId,
  session,
  onSessionChange,
  onFinished,
}: {
  traineeId: number
  session: WorkoutSession
  onSessionChange: (session: WorkoutSession | null) => void
  onFinished: () => void
}) {
  const { t } = useTranslation()
  const [local, setLocal] = useState(session)
  const [groupIndex, setGroupIndex] = useState(() => indexFromSession(session))
  const [actualsByLogId, setActualsByLogId] = useState<LogActuals>(() => {
    const map: LogActuals = {}
    for (const ex of session.exercises) {
      map[ex.id] = logActualFromExercise(ex)
    }
    return map
  })
  const [historyByName, setHistoryByName] = useState<Record<string, WorkoutExerciseHistoryEntry[]>>({})
  const [saving, setSaving] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const saveTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const actualsRef = useRef(actualsByLogId)
  const localRef = useRef(local)
  const groupIndexRef = useRef(groupIndex)

  const groups = groupWorkoutExercises(local.exercises)
  const currentGroup = groups[groupIndex]
  actualsRef.current = actualsByLogId
  localRef.current = local
  groupIndexRef.current = groupIndex

  useEffect(() => {
    if (session.id !== localRef.current.id) {
      localRef.current = session
      setLocal(session)
      setGroupIndex(indexFromSession(session))
      const map: LogActuals = {}
      for (const ex of session.exercises) {
        map[ex.id] = logActualFromExercise(ex)
      }
      setActualsByLogId(map)
      return
    }
    const dirty = dirtyLogIdsFromActuals(localRef.current.exercises, actualsRef.current)
    const merged = mergeServerSession(localRef.current, session, dirty)
    localRef.current = merged
    setLocal(merged)
    setActualsByLogId(mergeActualsPreservingDirty(merged, actualsRef.current, dirty))
  }, [session])

  const historyExerciseNamesKey = currentGroup
    ? [...new Set(currentGroup.exercises.map(ex => ex.exerciseName).filter(Boolean))].join('|')
    : ''

  useEffect(() => {
    const names = historyExerciseNamesKey ? historyExerciseNamesKey.split('|') : []
    if (names.length === 0) return
    let cancelled = false
    Promise.all(
      names.map(name =>
        workoutApi.coachGetExerciseHistory(traineeId, name)
          .then(res => [name, res.entries] as const)
          .catch(() => [name, [] as WorkoutExerciseHistoryEntry[]] as const),
      ),
    ).then((rows) => {
      if (cancelled) return
      const next: Record<string, WorkoutExerciseHistoryEntry[]> = {}
      for (const [name, entries] of rows) next[name] = entries
      setHistoryByName(next)
    })
    return () => { cancelled = true }
  }, [traineeId, historyExerciseNamesKey])

  async function saveCurrentGroup(actuals = actualsByLogId) {
    const current = localRef.current
    const group = groupWorkoutExercises(current.exercises)[groupIndexRef.current]
    if (!group || current.finishedAt) return true
    setSaving(true)
    setError(null)
    try {
      const result = await saveDirtyLogs(
        coachWorkoutMutations,
        current,
        group.exercises,
        actuals,
      )
      localRef.current = result.session
      setLocal(result.session)
      onSessionChange(result.session)
      setActualsByLogId(mergeActualsPreservingDirty(
        result.session,
        actuals,
        result.ok ? new Set() : result.remainingDirty,
      ))
      if (!result.ok) {
        setError(t.workout.conflict.log)
        return false
      }
      return true
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.workout.error.saveFailed)
      return false
    } finally {
      setSaving(false)
    }
  }

  function scheduleSave(logId: number) {
    const existing = saveTimersRef.current.get(logId)
    if (existing) clearTimeout(existing)
    saveTimersRef.current.set(logId, setTimeout(() => {
      saveTimersRef.current.delete(logId)
      void saveCurrentGroup(actualsRef.current)
    }, SAVE_DEBOUNCE_MS))
  }

  async function goToGroup(newIndex: number) {
    if (newIndex < 0 || newIndex >= groups.length || newIndex === groupIndex) return
    for (const timer of saveTimersRef.current.values()) clearTimeout(timer)
    saveTimersRef.current.clear()
    await saveCurrentGroup()
    const latest = localRef.current
    const nextGroups = groupWorkoutExercises(latest.exercises)
    const targetLog = nextGroups[newIndex]?.exercises[0]
    if (!targetLog) return
    const dirty = dirtyLogIdsFromActuals(latest.exercises, actualsRef.current)
    try {
      const result = await refreshAndSetCurrent(
        coachWorkoutMutations,
        latest,
        targetLog.id,
        dirty,
      )
      localRef.current = result.session
      setLocal(result.session)
      onSessionChange(result.session)
      if (result.staleSession) {
        setError(t.workout.conflict.session)
        return
      }
      setGroupIndex(newIndex)
      setActualsByLogId(mergeActualsPreservingDirty(
        result.session,
        actualsRef.current,
        dirty,
        nextGroups[newIndex]?.exercises.map(ex => ex.id),
      ))
      setError(null)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.workout.error.saveFailed)
      setGroupIndex(newIndex)
    }
  }

  async function finish() {
    setFinishing(true)
    try {
      for (const timer of saveTimersRef.current.values()) clearTimeout(timer)
      saveTimersRef.current.clear()
      await saveCurrentGroup().catch(() => undefined)
      await workoutApi.coachFinishSession(localRef.current.id)
      captureEvent('workout_finished', {
        session_id: localRef.current.id,
        source: 'pro_view',
      })
      onSessionChange(null)
      onFinished()
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.workout.error.finishFailed)
    } finally {
      setFinishing(false)
    }
  }

  async function cancel() {
    if (!confirm(t.workout.cancelConfirm)) return
    setCanceling(true)
    setError(null)
    try {
      for (const timer of saveTimersRef.current.values()) clearTimeout(timer)
      saveTimersRef.current.clear()
      await workoutApi.coachCancelSession(localRef.current.id)
      captureEvent('workout_cancelled', {
        session_id: localRef.current.id,
        source: 'pro_view',
      })
      onSessionChange(null)
      onFinished()
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.workout.error.cancelFailed)
    } finally {
      setCanceling(false)
    }
  }

  if (!currentGroup) return null

  const firstName = currentGroup.exercises[0]?.exerciseName
  const history = firstName ? (historyByName[firstName] ?? []) : []

  return (
    <WorkoutSessionActiveView
      blockName={local.blockName}
      compact
      hideNavHint
      groupIndex={groupIndex}
      groups={groups}
      actualsByLogId={actualsByLogId}
      history={history}
      historyByExerciseName={historyByName}
      historySessionId={local.id}
      saving={saving}
      finishing={finishing}
      canceling={canceling}
      finished={Boolean(local.finishedAt)}
      error={error}
      onActualChange={(logId, next) => {
        setActualsByLogId(prev => {
          const merged = { ...prev, [logId]: next }
          actualsRef.current = merged
          return merged
        })
        scheduleSave(logId)
      }}
      onPrev={() => void goToGroup(groupIndex - 1)}
      onNext={() => void goToGroup(groupIndex + 1)}
      onFinish={() => void finish()}
      onCancel={() => void cancel()}
    />
  )
}
